const { getElement } = window.BAO.Utils

class PushCartError {
  description = ''

  status = null

  MESSAGES = {
    ADD_TOO_MANY: window.theme.strings.pushCart.addTooMany,
    ALREADY_HAVE_ALL_STOCK: window.theme.strings.pushCart.alreadyHaveAllStock,
    CANNOT_FIND_VARIANT: window.theme.strings.pushCart.cannotFindVariant,
    GENERIC: window.theme.strings.pushCart.generic,
    NOT_ENOUGH_IN_STOCK: window.theme.strings.pushCart.notEnoughInStock,
    SOLD_OUT: window.theme.strings.pushCart.soldOut,
  }

  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.description = error.description
    this.status = error.status

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone = this.pushCart.els['error-template'].element.content.cloneNode(
      true,
    )
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description?.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description?.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors.toLowerCase() === 'bag error'
  }

  get soldOut () {
    return this.description?.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description?.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
  }

  hideFreeShippingOnChina () {
    if (localStorage.getItem('BAO-location-sunspel') !== 'CN') return

    const shippingElement = this.els?.shipping?.element
    if (!shippingElement) return

    shippingElement.hidden = true
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections
    this.hideFreeShippingOnChina()

    if (
      localStorage.getItem('BAO-location-sunspel') &&
      localStorage.getItem('BAO-location-sunspel') === 'CN'
    ) {
      const shippingElement = this.els?.shipping?.element
      if (shippingElement) {
        shippingElement.hidden = true
      }
    }

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onBeforeCartAdd,
    )
  }

  onBeforeCartAdd () {
    this.drawers.activeDrawerKey = this.drawer.getAttribute('key')

    this.loading = true
  }

  onAfterCartAdd (event) {
    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    const productId = event.detail?.formData?.get('ometria_product_id')
    const quantity = event.detail?.formData?.get('quantity')

    if (window?.ometria && productId && quantity) {
      window.ometria.trackAddToBasket?.(productId, quantity)
    }

    this.loading = false
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawers () {
    return this.closest('site-drawers')
  }

  get drawer () {
    return this.closest('site-drawer')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
